import { baseApi } from './baseApi';
import { normalizeKeys } from '../../utils/transform';

export interface Agent {
  id: string;
  name: string;
  description: string;
  scope: 'global' | 'organisation' | 'application';
  version: string;
  versionStatus: 'draft' | 'sandbox' | 'published';
  status: string;
  agentType?: string;
  llmProvider?: string;
  llmModel?: string;
  visibility?: string;
  applicationCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentDetail extends Agent {
  stats: {
    totalSessions: number;
    totalExchanges: number;
    avgResponseTime: number;
    activeApplications: number;
  };
  versions: VersionEntry[];
  applications: AgentApplication[];
}

export interface VersionEntry {
  version: string;
  status: 'draft' | 'sandbox' | 'published';
  date: string;
  description?: string;
  changeCount?: number;
}

export interface AgentApplication {
  id: string;
  name: string;
  appId: string;
  status: string;
}

function unwrapAgents(response: unknown): Agent[] {
  const normalized = normalizeKeys<{
    agents?: Array<Record<string, unknown>>;
    response?: { agents?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>;
  }>(response);

  const rawList =
    normalized.agents ??
    (Array.isArray(normalized.response)
      ? normalized.response
      : normalized.response?.agents) ??
    [];

  return rawList.map((item, index) => {
    const agentName = String(item.agentName ?? item.name ?? `agent-${index}`);
    const visibility = String(item.visibility ?? 'public').toLowerCase();
    let scope: Agent['scope'] = 'organisation';
    if (visibility === 'public' || visibility === 'global') {
      scope = 'global';
    } else if (visibility === 'application' || visibility === 'app') {
      scope = 'application';
    }

    return {
      id: agentName,
      name: agentName,
      description:
        String(item.description ?? '') ||
        [item.llmProvider, item.llmModel].filter(Boolean).join(' · ') ||
        String(item.agentType ?? 'Agent'),
      scope,
      version: String(item.version ?? item.llmModel ?? '—'),
      versionStatus: 'published',
      status: 'active',
      agentType: item.agentType ? String(item.agentType) : undefined,
      llmProvider: item.llmProvider ? String(item.llmProvider) : undefined,
      llmModel: item.llmModel ? String(item.llmModel) : undefined,
      visibility,
    };
  });
}

export const agentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<Agent[], { scope?: string } | void>({
      // Runtime agents list (org-scoped via x-organization-id header)
      query: () => '/agents',
      transformResponse: (response: unknown) => unwrapAgents(response),
      providesTags: ['Agent'],
    }),

    getAgent: builder.query<AgentDetail, string>({
      query: (id) => `/portal/agents/${id}`,
      transformResponse: (response: unknown) => normalizeKeys<AgentDetail>(response),
      providesTags: ['Agent'],
    }),

    createAgent: builder.mutation<Agent, { name: string; description: string; scope: string }>({
      query: (body) => ({
        url: '/portal/agents',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Agent'],
    }),

    updateAgentConfig: builder.mutation<Agent, { id: string; config: Record<string, unknown> }>({
      query: ({ id, config }) => ({
        url: `/portal/agents/${id}/config`,
        method: 'PUT',
        body: { config },
      }),
      invalidatesTags: ['Agent'],
    }),

    publishAgent: builder.mutation<Agent, string>({
      query: (id) => ({
        url: `/portal/agents/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: ['Agent'],
    }),

    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({
        url: `/portal/agents/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Agent'],
    }),
  }),
});

export const {
  useGetAgentsQuery,
  useGetAgentQuery,
  useCreateAgentMutation,
  useUpdateAgentConfigMutation,
  usePublishAgentMutation,
  useDeleteAgentMutation,
} = agentsApi;
